import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {Button, Card, Col, DatePicker, Form, Radio, Row, Select} from 'antd';
import dayjs, {Dayjs} from 'dayjs';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import '../assets/css/Calendar.css'
import {momentLocalizer, SlotInfo} from 'react-big-calendar';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import {CaretDownOutlined, CaretUpOutlined, LeftOutlined, PlusOutlined, RightOutlined} from '@ant-design/icons';
import {EventInteractionArgs,} from 'react-big-calendar/lib/addons/dragAndDrop';
import {accessibilityRoles} from "../utils/accessUtils";
import {
    getDoctorColor,
    mapCalendarEventsToWashEvents,
    mapWashEventToCalendarEvent,
} from "../utils/calendarUtils";
import AccessControl from "../components/common/AccessControl";
import {FormattedMessage, useIntl} from "react-intl";
import {
    MedicalStaff,
    ClinicResponse,
    UserRole,
} from "../api-client";
import {
    getCalendarApi,
    getClinicsApi,
    getMedicalStaffApi,
    getPatientApi,
} from "../api/apiInstances";
import {isMobileOnly} from "react-device-detect";
import EventModalWrapper from "../components/calendar/EventModalWrapper";
import moment from 'moment';
import 'moment/locale/bg';
import {DentobookApplicationContext} from "../context/DentobookApplicationContext";
import bg from 'antd/es/date-picker/locale/bg_BG';
import en from 'antd/es/date-picker/locale/en_US';
import 'dayjs/locale/bg';
import 'dayjs/locale/en';
import FormItem from "antd/es/form/FormItem";
import {WashEvent} from "../types";
import MemoizedCalendar , { CustomEvent } from "../components/calendar/MemoizedCalendar";
import {AlertContext} from "../context/AlertContext";
import { debounce } from 'lodash';
import logger from "../utils/logger";
import {useSearchParams} from "react-router-dom";

interface CalendarPageProps {
    // Your prop types here, if any
}

// Wrapper so the freed-slot pseudo-event renders a clean minimal block instead of
// being parsed by CustomEvent's title.split('\n') patient/doctor extraction logic.
const CalendarEventComponent = (props: any) => {
    if (props.event?.id === 'freed-slot-flash') {
        return (
            <div style={{ padding: '2px 4px', fontSize: 11, lineHeight: 1.4, pointerEvents: 'none' }}>
                <div style={{ fontStyle: 'italic' }}>{props.event.title}</div>
            </div>
        );
    }
    return <CustomEvent {...props} />;
};

const CalendarPage: React.FC<CalendarPageProps> = () => {
    const intl = useIntl();
    const {principal} = React.useContext(DentobookApplicationContext);
    const [radioValue, setRadioValue] = useState(1);
    const {locale} = React.useContext(DentobookApplicationContext);
    dayjs.locale(locale);
    const localizer = momentLocalizer(moment);
    const [appointments, setAppointments] = useState<WashEvent[]>([]);
    const sortBy: 'id' | 'name' = 'id';
    const [calendarDate, setCalendarDate] = useState<Dayjs>(dayjs().startOf('day'));
    const [isDatePickerOpen, setDatePickerOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState<WashEvent | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedPatient, setSelectedPatient] = useState<string>('');
    const [selectedDoctor, setSelectedDoctor] = useState<string>('');
    const [selectedRoom, setSelectedRoom] = useState<string>('');
    const [medicalStaff, setMedicalStaff] = useState<MedicalStaff[]>([]);
    const [clinics, setClinics] = useState<ClinicResponse[]>([]);
    const [keyword,] = useState<string>('');
    const [form] = Form.useForm();
    const [allAppointments, setAllAppointments] = useState<WashEvent[]>([]);
    const calendarApi = getCalendarApi();
    const clinicApiFactory = getClinicsApi();
    const medicalStaffApi = getMedicalStaffApi();
    const patientApi = getPatientApi();
    const [currentLocaleConfig, setCurrentLocaleConfig] = useState(locale === 'BG' ? bg : en);
    const [selectOptions, setSelectOptions] = useState<SelectOption[]>([]);
    const {setVisible, setError ,setWarning} = useContext(AlertContext);
    const [searchParams] = useSearchParams();
    // Prevent re-opening the modal if loadEvents() fires again after the user closed it
    const deepLinkHandledRef = useRef(false);
    // Freed-slot flash: temporary green pseudo-event shown after clicking a cancelled notification
    const [freedSlotEvent, setFreedSlotEvent] = useState<WashEvent | null>(null);
    const freedSlotProcessedRef = useRef(false);

    useEffect(() => {
        switch (locale) {
            case 'BG':
                setCurrentLocaleConfig(bg);
                break;
            case 'EN':
                setCurrentLocaleConfig(en);
                break;
            default:
                setCurrentLocaleConfig(en);
                break;

        }
    }, [locale]);

    useEffect(() => {
        loadEvents();
        loadMedicalStaff();
        loadRooms();
    }, []);

    type SelectOption = {
        value: string;
        label: string;
    };

    useEffect(() => {
        if (radioValue === 1) {
            const doctorsWithMedicalRole = medicalStaff.filter(doctor => doctor.role === UserRole.MedicalDoctor || doctor.role === UserRole.ClinicAdmin  || doctor.role === UserRole.MedicalAssistant);
            setSelectOptions(doctorsWithMedicalRole.map(doctor => ({
                value: doctor.id!,
                label: `${doctor.firstName} ${doctor.lastName}`
            })).sort((doctorOne, doctorTwo) => doctorOne.label.localeCompare(doctorTwo.label)));
        } else if (radioValue === 2) {
            setSelectOptions(clinics.filter(room => room.name).map(room => ({
                value: room.id!,
                label: room.name!
            })).sort((cabinetOne, cabinetTwo) => cabinetOne.label.localeCompare(cabinetTwo.label)));
        }
    }, [radioValue, medicalStaff, clinics]);



    const setCurrentDate = () => {
        setCalendarDate(dayjs());
    };

    const setCurrentWeek = () => {
        const today = dayjs();
        const dayOfWeek = today.day();
        const startOfWeek = today.subtract(dayOfWeek - 1, 'day');
        setCalendarDate(startOfWeek);
    };

    const setNextWeek = () => {
        setCalendarDate(calendarDate.add(1, 'week'));
    };

    const setLastWeek = () => {
        setCalendarDate(calendarDate.add(-1, 'week'));
    };

    const setNextDay = () => {
        setCalendarDate(calendarDate.add(1, 'day'));
    };

    const setPreviousDay = () => {
        setCalendarDate(calendarDate.add(-1, 'day'));
    };

    const loadEvents = () => {
        calendarApi.fetchAllCalendarEvents().then((response) => {
            const events = mapCalendarEventsToWashEvents(response.data);
            setAllAppointments(events);
            return setAppointments(events);
        });
    }

    // Deep-link: /calendar?eventId=<uuid> — navigate to the event's date and open its modal.
    // deepLinkHandledRef prevents the modal from re-opening when loadEvents() refreshes
    // allAppointments (e.g., after a drag-and-drop update).
    useEffect(() => {
        const eventId = searchParams.get('eventId');
        if (!eventId || allAppointments.length === 0 || deepLinkHandledRef.current) return;
        const targetEvent = allAppointments.find(ev => ev.id === eventId);
        if (!targetEvent) return;
        deepLinkHandledRef.current = true;
        setCalendarDate(dayjs(targetEvent.start));
        setSelectedEvent(targetEvent);
        setSelectedPatient(targetEvent.patient || '');
        setIsModalOpen(true);
    }, [allAppointments, searchParams]);

    // Freed-slot flash: consume sessionStorage payload once after events load.
    // Shows a temporary green pseudo-event for ~10s to highlight the freed time slot.
    useEffect(() => {
        if (freedSlotProcessedRef.current) return;
        const raw = sessionStorage.getItem('dentobook_freed_slot');
        if (!raw) return;
        freedSlotProcessedRef.current = true;
        sessionStorage.removeItem('dentobook_freed_slot');
        try {
            const payload = JSON.parse(raw);
            if (!payload.startIso) return;
            const start = new Date(payload.startIso);
            const end = payload.endIso ? new Date(payload.endIso) : new Date(start.getTime() + 30 * 60 * 1000);
            const pseudo: WashEvent = {
                id: 'freed-slot-flash',
                title: intl.formatMessage({id: 'freed_slot_flash'}),
                googleId: '',
                syncStatus: '',
                start,
                end,
                user: 0,
                patient: '',
                doctor: '',
                clinicId: payload.clinicId ?? '',
                color: '#52c41a99',
                createdAt: new Date().toISOString(),
            };
            setFreedSlotEvent(pseudo);
            setCalendarDate(dayjs(start));
            setTimeout(() => setFreedSlotEvent(null), 10000);
        } catch {
            // ignore parse errors — flash is best-effort
        }
    }, []);

    const loadRooms = () => {
        clinicApiFactory.listClinics()
            .then(response => setClinics(response.data))
            .catch(error => logger.error("Failed to load rooms:", error));
    };

    const loadMedicalStaff = () => {
        medicalStaffApi.fetchAllMedicalStaff('', sortBy, 1, 10000)
            .then(response => {
                if (!response.data || !Array.isArray(response.data.medicalStaff)) {
                    throw new Error('Invalid response data');
                }

                setMedicalStaff(response.data.medicalStaff);
            })
            .catch(error => {
                logger.error("Error fetching medical staff:", error);
            });
    };

    const handleDoctorChange = (value: string) => {
        setSelectedDoctor(value);
    };

    const handleRoomChange = (value: string) => {
        setSelectedRoom(value);
    };

    const closeModal = () => {
        setSelectedEvent(null);
        setSelectedPatient('');
        setSelectedDoctor('');
        setSelectedRoom('')
        setIsModalOpen(false);
    };

    const handleEventDrop = useCallback(
        async ({ event, start, end }: EventInteractionArgs<WashEvent>) => {
            const startDate = start instanceof Date ? start : new Date(start);
            const endDate = end instanceof Date ? end : new Date(end);

            // Step 1: Optimistically update UI
            setAppointments((prevAppointments) =>
                prevAppointments.map((item) =>
                    item.createdAt === event.createdAt
                        ? { ...item, start: startDate, end: endDate }
                        : item
                )
            );

            // Step 2: Debounced API Update
            debouncedUpdateEvent(event, startDate, endDate);
        },
        [appointments]
    );

// Step 3: Debounced API logic
    const debouncedUpdateEvent = useMemo(
        () =>
            debounce(async (event: WashEvent, start: Date, end: Date) => {
                try {
                    const updatedEvent = { ...event, start, end };
                    const updatedCalendarEvent = mapWashEventToCalendarEvent(updatedEvent);


                    // Update local calendar event (backend will handle Google sync via sync_status)
                    await calendarApi.updateCalendarEvent(updatedEvent.id, updatedCalendarEvent);
                    loadEvents();
                    setError('');
                    setVisible(false);
                } catch (error: any) {
                    logger.error('Error updating event:', error);
                }
            }, 200), // 200ms debounce delay
        [intl]
    );

    const handleSelectSlot = (slotInfo: SlotInfo) => {
        const openingHour = 0;
        const closingHour = 24;

        const startHour = slotInfo.start.getHours();
        const endHour = slotInfo.end.getHours();

        // Prevent selection outside of working hours
        if (startHour < openingHour || endHour > closingHour || (endHour === closingHour && slotInfo.end.getMinutes() > 0)) {
            alert("Cannot create or modify events outside of working hours (0:00 AM to 24:00 PM).");
            return;
        }
        // Find existing event if it exists based on the start and end times
        const existingEvent = appointments.find(
            event =>

                event.start.getTime() === slotInfo.start.getTime() &&
                event.end.getTime() === slotInfo.end.getTime()
        );

        if (existingEvent) {
            setSelectedEvent(existingEvent);
            setSelectedPatient(existingEvent.patient);
            setSelectedDoctor(existingEvent.doctor);
            setSelectedRoom(existingEvent.clinicId);
        } else {
            // Create a new appointment
            const appointmentId = new Date().getTime().toString();
            const newAppointment: WashEvent = {
                id: '',
                syncStatus: '',
                title: 'Добави час',
                googleId: '',
                start: slotInfo.start,
                end: slotInfo.end,
                user: 0,
                patient: selectedPatient,
                doctor: selectedDoctor,
                clinicId: selectedRoom,
                color: '',
                createdAt: appointmentId,
            };
            setSelectedEvent({...newAppointment, createdAt: appointmentId});
            setSelectedPatient(''); // Clear the selected patient for new events
        }
        setIsModalOpen(true);
    };

    const createDefaultAppointment = () => {
        const now = new Date();
        const oneHourLater = new Date(now.getTime() + 60 * 60 * 1000); // 1 hour later

        const appointmentId = new Date().getTime().toString();
        const newAppointment: WashEvent = {
            id: '',
            syncStatus: '',
            title: 'Добави час',
            googleId: '',
            start: now,
            end: oneHourLater,
            user: 0,
            patient: selectedPatient,
            doctor: selectedDoctor,
            clinicId: selectedRoom,
            color: '',
            createdAt: appointmentId,
        };
        setSelectedEvent({...newAppointment, createdAt: appointmentId});
        setSelectedPatient('');
        setIsModalOpen(true);
    };


    const medicalDoctorIds = medicalStaff
        .filter(staff => staff.role === UserRole.MedicalDoctor || staff.role === UserRole.ClinicAdmin || staff.role === UserRole.MedicalAssistant)
        .map(doctor => doctor.id);

    const desktopNavigation =
        <>
            <Row className="mb-2">
                <Col span={12}>
                    <h3 className="styledH3">
                        <FormattedMessage id="calendar"/>
                    </h3>
                </Col>
                <Col span={12}>
                    <div style={{ float: 'right', marginTop: '16px' }}>
                        <Button
                            shape="round"
                            className={"dentobook-btn-primary dentobook-btn-medium"}
                            type="primary"
                            onClick={createDefaultAppointment}
                        >
                            <PlusOutlined/> <FormattedMessage id="add"/>
                        </Button>
                    </div>
                </Col>
            </Row>
            <Row className="mb-2">
                <Col span={2}>
                    <Button
                        shape="round"
                        className={"calendar-btn-secondary dentobook-btn-medium"}
                        type="primary"
                        onClick={isMobileOnly ? setCurrentDate : setCurrentWeek}
                    >
                        <FormattedMessage id="today"/>
                    </Button>
                </Col>
                <Col span={10}>
                    <div style={{display: 'flex', alignItems: 'center'}}>
                        <Button onClick={isMobileOnly ? setPreviousDay : setLastWeek} type="text" size={"large"}>
                            <LeftOutlined className="icon-24px"/>
                        </Button>
                        <Button onClick={isMobileOnly ? setNextDay : setNextWeek} type="text" size={"large"}>
                            <RightOutlined className="icon-24px"/>
                        </Button>
                        {/* Adjust marginLeft here */}
                        <div style={{marginLeft: '10px'}}>
                            <DatePicker
                                locale={currentLocaleConfig}
                                size={"large"}
                                variant="borderless"
                                className="dentobook-calendar-datepicker desktop"
                                format={"MMMM YYYY"}
                                value={calendarDate}
                                onChange={e => {
                                    if (e === null) {
                                        setCalendarDate(dayjs().startOf('month'));
                                    } else {
                                        const formattedDate = capitalizeFirstLetter(e.format("MMMM YYYY"));
                                        setCalendarDate(dayjs(formattedDate, "MMMM YYYY"));
                                    }
                                }}
                                placeholder=""
                                open={isDatePickerOpen}
                                onOpenChange={status => setDatePickerOpen(status)}
                                suffixIcon={isDatePickerOpen ? <CaretUpOutlined/> : <CaretDownOutlined/>}
                            />
                        </div>
                    </div>
                </Col>
                <Col span={12}>
                    <div style={{float: 'right'}}>
                        <Radio.Group
                            onChange={(e) => {
                                setRadioValue(e.target.value);
                            }}
                            value={radioValue}
                        >
                            <Radio className="radio-text" value={1}><FormattedMessage id="doctors"/></Radio>
                            <Radio className="radio-text" value={2}><FormattedMessage id="clinics"/></Radio>
                        </Radio.Group>

                        <Select
                            showSearch={false}
                            suffixIcon={<CaretDownOutlined/>}
                            className="dentobook-calendar-select"
                            mode="multiple"
                            placeholder={intl.formatMessage({id: 'generalCalendar'})}
                            maxTagCount={0}
                            onChange={val => {
                                if (val && val.length > 0) {
                                    if (radioValue === 1) {
                                        const validDoctorIds = val.filter((id: string | undefined): id is string => id !== undefined && medicalDoctorIds.includes(id));
                                        const filteredAppointments = allAppointments.filter(appointment =>
                                            appointment.doctor && validDoctorIds.includes(appointment.doctor)
                                        );
                                        setAppointments(() => [...filteredAppointments]);
                                    } else if (radioValue === 2) {
                                        const filteredAppointments = allAppointments.filter(appointment =>
                                            val.includes(appointment.clinicId)
                                        );
                                        setAppointments(() => [...filteredAppointments]);
                                    }
                                } else {
                                    loadEvents();
                                }
                            }}
                        >
                            {selectOptions.map(option => (
                                <Select.Option key={option.value} value={option.value}>
                                    {option.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>
                </Col>
            </Row>
        </>

    const mobileNavigation =
        <>
            <Row>
                <Col span={24}>
                    <h3 className="styledH3">
                        <FormattedMessage id="calendar"/>
                    </h3>
                </Col>
            </Row>
            <Row style={{marginBottom: '24px'}}>
                <Col span={24}>
                    <Radio.Group
                        onChange={(e) => {
                            setRadioValue(e.target.value);
                        }}
                        value={radioValue}
                    >
                        <Radio className="radio-text" value={1}><FormattedMessage id="doctors"/></Radio>
                        <Radio className="radio-text" value={2}><FormattedMessage id="clinics"/></Radio>
                    </Radio.Group>
                </Col>
            </Row>
            <Row>
                <Col span={24}>
                    <FormItem className="dentobook-form-item">
                        <Select
                            className="dentobook-input"
                            mode="multiple"
                            placeholder={intl.formatMessage({id: 'generalCalendar'})}
                            maxTagCount={0}
                            onChange={val => {
                                if (val && val.length > 0) {
                                    if (radioValue === 1) {
                                        // Filter for valid doctor IDs
                                        const validDoctorIds = val.filter((id: string | undefined): id is string => id !== undefined && medicalDoctorIds.includes(id));
                                        // Filter appointments based on these IDs
                                        const filteredAppointments = allAppointments.filter(appointment =>
                                            appointment.doctor && validDoctorIds.includes(appointment.doctor)
                                        );
                                        setAppointments(() => [...filteredAppointments]);


                                    } else if (radioValue === 2) {
                                        // Filter appointments based on selected clinic IDs
                                        const filteredAppointments = allAppointments.filter(appointment =>
                                            val.includes(appointment.clinicId)
                                        );
                                        setAppointments(() => [...filteredAppointments]);

                                    }
                                } else {
                                    // Reload events if no values are selected
                                    loadEvents();
                                }
                            }}
                        >
                            {selectOptions.map(option => (
                                <Select.Option key={option.value} value={option.value}>
                                    {option.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </FormItem>
                </Col>
            </Row>
            <Row gutter={12} style={{marginBottom: '24px'}}>
                <Col span={6}>
                    <Button
                        shape="round"
                        className={"dentobook-btn-secondary dentobook-btn-medium"}
                        type="primary"
                        onClick={isMobileOnly ? setCurrentDate : setCurrentWeek}
                    >
                        <FormattedMessage id="today"/>
                    </Button>
                </Col>
                <Col span={18}>
                    <Button onClick={isMobileOnly ? setPreviousDay : setLastWeek} type="text" size={"large"}
                            style={{paddingLeft: '0'}}>
                        <LeftOutlined className="icon-24px"/>
                    </Button>
                    <Button onClick={isMobileOnly ? setNextDay : setNextWeek} type="text" size={"large"}
                            style={{paddingLeft: '0'}}>
                        <RightOutlined className="icon-24px"/>
                    </Button>
                    <DatePicker
                        locale={currentLocaleConfig}
                        className="dentobook-calendar-datepicker"
                        format={"DD.MM.YYYY"}
                        value={calendarDate}
                        onChange={e => {
                            if (e === null) {
                                setCalendarDate(dayjs().startOf('month'));
                            } else {
                                const formattedDate = e.format("DD/MM/YY");
                                setCalendarDate(dayjs(formattedDate, "DD/MM/YY"));
                            }
                        }}
                        placeholder=""
                        open={isDatePickerOpen}
                        onOpenChange={status => setDatePickerOpen(status)}
                        suffixIcon={isDatePickerOpen ? <CaretUpOutlined/> : <CaretDownOutlined/>}
                    />
                </Col>
            </Row>
            <div className={"mobile-btn-wrapper"}>
                <Button
                    shape="round"
                    className={"mobile-add-btn dentobook-btn-primary dentobook-btn-medium"}
                    type="primary"
                    onClick={createDefaultAppointment}
                >
                    <PlusOutlined/> <FormattedMessage id="add"/>
                </Button>
            </div>
        </>

    return (

        <AccessControl notAccessibleFor={[accessibilityRoles.PLATFORM_ADMINISTRATOR, accessibilityRoles.PATIENT]}>
            <Card
                bordered={false}
            >

                {isMobileOnly ? mobileNavigation : desktopNavigation}

                <MemoizedCalendar
                    components={{
                        event: CalendarEventComponent
                    }}
                    view={isMobileOnly}
                    onEventResize={handleEventDrop as any}
                    resizable={true}
                    localizer={localizer}
                    events={freedSlotEvent ? [...appointments, freedSlotEvent] : appointments}
                    onEventDrop={handleEventDrop as any}
                    onSelectSlot={handleSelectSlot}
                    date={calendarDate.toDate()}
                    onSelectEvent={(event: WashEvent | object, e: React.SyntheticEvent) => {
                        if (isWashEvent(event)) {
                            // Ignore clicks on the freed-slot pseudo-event
                            if (event.id === 'freed-slot-flash') return;
                            setSelectedEvent(event);
                            setSelectedPatient(event.patient);
                            setIsModalOpen(true);
                        }
                    }}
                    eventPropGetter={(event: any) => {
                        const washEvent = event as WashEvent;
                        const backgroundColor = washEvent.color ? washEvent.color : getDoctorColor(washEvent.doctor);
                        return {
                            style: {backgroundColor},
                            title: null
                        }
                    }}
                />

                {selectedEvent !== null && (
                    <div>
                        <EventModalWrapper
                            setAllAppointments={setAllAppointments}
                            isOpen={isModalOpen}
                            onClose={() => closeModal()}
                            selectedEvent={selectedEvent}
                            doctors={medicalStaff}
                            clinics={clinics}
                            onDoctorChange={handleDoctorChange}
                            onRoomChange={handleRoomChange}
                            form={form}
                            appointments={appointments}
                            setAppointments={setAppointments}
                            setSelectedEvent={setSelectedEvent}
                        />
                    </div>
                )}
                {isMobileOnly ? <div style={{marginBottom: 150}}></div> : null}
            </Card>
        </AccessControl>
    );
};

export default CalendarPage;

function capitalizeFirstLetter(str: string): string {
    return str.replace(/\b\w/g, char => char.toUpperCase());
}

function isWashEvent(event: WashEvent | object): event is WashEvent {
    return 'id' in event && 'title' in event && 'start' in event && 'end' in event;
}
